import React, {useEffect, useState} from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  VStack,
  Flex,
  Button,
  Divider,
} from '@chakra-ui/react';
import {TextG12, TextG14} from '../typography';
import UserAvatar from "../UserAvatar";
import {User} from "../../hooks/useAuth";
import UserSearchSelect from "./UserSearchSelect";
import {EntityRole} from "../../hooks/useEntityUserRole";
import {BlueButton} from "../button";
import {AxiosPromise} from "axios";

interface UsersListModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  assignUserRoleToEntity: (entityId: number, userId: number, roleId: number) => AxiosPromise<void>;
  removeUserRoleFromEntity: (entityId: number, userId: number) => AxiosPromise<void>;
  fetchUsersAndRoles: any;
  fetchUsersHook: any
  entityId: number;
}
const PotentialBusinessManagersModal: React.FC<UsersListModalProps> = ({
  isOpen,
  onClose,
  title,
  assignUserRoleToEntity,
  removeUserRoleFromEntity,
  fetchUsersAndRoles,
  fetchUsersHook,
  entityId,
}) => {
  const { data: usersAndRoles } = fetchUsersAndRoles(entityId);

  const [managers, setManagers] = useState<User[]>([]);
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [term, setTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    if(usersAndRoles) {
      const managers = usersAndRoles?.data.filter((ur) => ur.role_id === EntityRole.MANAGER).map((ur) => ur.user) || [];
      const teamMembers = usersAndRoles?.data.filter((ur) => ur.role_id === EntityRole.TEAM_MEMBER).map((ur) => ur.user) || [];

      setManagers(managers);
      setTeamMembers(teamMembers);
    }
  }, [usersAndRoles]);

  const {data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading} = fetchUsersHook(entityId, term);

  const users = data?.pages.flatMap((page) => page.data.data) || [];

  async function addUser(role: EntityRole) {
    if (!selectedUser) return;

    await assignUserRoleToEntity(entityId, selectedUser.id, role);

    if(role === EntityRole.MANAGER) {
      setManagers((prev) => [...prev, selectedUser]);
    }
    else {
      setTeamMembers((prev) => [...prev, selectedUser]);
    }
    setSelectedUser(null);
    setTerm('');
  }

  async function removeTeamMember(userId: number) {
    await removeUserRoleFromEntity(entityId, userId);

    setTeamMembers((prev) => prev.filter((u) => u.id !== userId));
  }

  function ManagerRow({ user }: { user: User }) {
    return (
      <Flex
        align="center"
        justify="space-between"
        p={3}
        border="1px solid"
        borderColor="gray.100"
        borderRadius="8px"
      >
        <Flex align="center" gap={3}>
          <UserAvatar
            user={user}
            avatar_image={user.profilePhotoUrl}
            w="40px"
            h="40px"
          />
          <Box>
            <TextG14 fontWeight="600">{user.name}</TextG14>
            {user.handle && (
              <TextG12 color="gray.500">@{user.handle}</TextG12>
            )}
          </Box>
        </Flex>
      </Flex>
    );
  }
  function TeamMemberRow({ user }: { user: User }) {
    return (
      <Flex
        align="center"
        justify="space-between"
        p={3}
        border="1px solid"
        borderColor="gray.100"
        borderRadius="8px"
      >
        <Flex align="center" gap={3}>
          <UserAvatar
            user={user}
            avatar_image={user.profilePhotoUrl}
            w="40px"
            h="40px"
          />
          <Box>
            <TextG14 fontWeight="600">{user.name}</TextG14>
            {user.handle && (
              <TextG12 color="gray.500">@{user.handle}</TextG12>
            )}
          </Box>
        </Flex>

        <Button
          size="sm"
          variant="ghost"
          colorScheme="red"
          onClick={() => removeTeamMember(user.id)}
        >
          Remove
        </Button>
      </Flex>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="80vh">
        <ModalHeader>
          <TextG14 fontWeight="600">{title}</TextG14>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={4}>
          <TextG14 fontWeight="600" mb={2}>
            Business Managers
          </TextG14>

          <VStack spacing={2} align="stretch" mb={6}>
            {managers.length === 0 ? (
              <TextG14 color="gray.500">No managers yet</TextG14>
            ) : (
              managers.map((user) => (
                <ManagerRow key={user.id} user={user} />
              ))
            )}
          </VStack>

          <Divider mb={4} />

          <TextG14 fontWeight="600" mb={2}>
            Team Members
          </TextG14>

          <VStack spacing={2} align="stretch" mb={6}>
            {teamMembers.length === 0 ? (
              <TextG14 color="gray.500">No team members yet</TextG14>
            ) : (
              teamMembers.map((user) => (
                <TeamMemberRow key={user.id} user={user} />
              ))
            )}
          </VStack>

          <Divider mb={4} />

          <TextG14 fontWeight="600" mb={2}>
            Add user
          </TextG14>

          <UserSearchSelect
            term={term}
            setTerm={setTerm}
            users={users}
            isLoading={isLoading}
            onSelect={setSelectedUser}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />

          {selectedUser && (
            <Box mt={4}>
              <Flex align="center" justify="space-between">
                <TextG14>Add new user <strong>{selectedUser.name}</strong> as:</TextG14>
              </Flex>
              <Flex align="center" justify="space-between">
                <BlueButton size="sm" onClick={() => addUser(EntityRole.MANAGER)}>
                  Business manager
                </BlueButton>
                <BlueButton size="sm" onClick={() => addUser(EntityRole.TEAM_MEMBER)}>
                  Team member
                </BlueButton>
              </Flex>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PotentialBusinessManagersModal;
